import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Ticket, Trash2, Tag, Loader2, ShieldCheck, Minus, Plus } from 'lucide-react';
import { usePublicStore } from '@store/publicStore';
import { publicApi } from '@services/publicApi';
import { formatCurrency } from '@utils';
import toast from 'react-hot-toast';

const checkoutSchema = z.object({
  buyerName: z.string().min(2, 'Name must be at least 2 characters'),
  buyerEmail: z.string().email('Please enter a valid email'),
  buyerPhone: z.string().optional(),
});

type CheckoutForm = z.infer<typeof checkoutSchema>;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cartItems, cartEventId, cartEventSlug, couponCode, couponDiscount,
    buyerName, buyerEmail, buyerPhone,
    updateQuantity, removeFromCart, clearCart,
    setCoupon, clearCoupon, setBuyerInfo,
    getCartTotal, getDiscountAmount,
  } = usePublicStore();

  const [couponInput, setCouponInput] = useState(couponCode || '');
  const [couponLoading, setCouponLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const subtotal = cartItems.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const discount = getDiscountAmount();
  const total = getCartTotal();

  const { register, handleSubmit, formState: { errors } } = useForm<CheckoutForm>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      buyerName: buyerName || '',
      buyerEmail: buyerEmail || '',
      buyerPhone: buyerPhone || '',
    },
  });

  const handleApplyCoupon = async () => {
    if (!couponInput.trim() || !cartEventId) return;
    setCouponLoading(true);
    try {
      const res = await publicApi.validateCoupon(couponInput.trim(), cartEventId);
      if (res.data) {
        setCoupon(couponInput.trim(), {
          type: res.data.type,
          value: res.data.value,
          maxDiscount: res.data.maxDiscount,
        });
        toast.success('Coupon applied!');
      } else {
        toast.error(res.message || 'Invalid coupon');
      }
    } catch {
      toast.error('Invalid or expired coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    clearCoupon();
    setCouponInput('');
  };

  const onSubmit = async (formData: CheckoutForm) => {
    if (!cartEventId || cartItems.length === 0) return;

    setBuyerInfo(formData.buyerName, formData.buyerEmail, formData.buyerPhone || '');
    setSubmitting(true);

    try {
      const res = await publicApi.createCheckout({
        event_id: cartEventId,
        items: cartItems.map(i => ({
          ticket_type_id: i.ticketTypeId,
          quantity: i.quantity,
        })),
        buyer_name: formData.buyerName,
        buyer_email: formData.buyerEmail,
        buyer_phone: formData.buyerPhone || undefined,
        coupon_code: couponCode || undefined,
      });

      const { order, redirectUrl, free } = res.data;

      if (free || !redirectUrl) {
        clearCart();
        navigate(`/order/${order.orderReference}`);
      } else {
        // Redirect to Tap payment page
        window.location.href = redirectUrl;
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Checkout failed. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Empty cart state
  if (cartItems.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
            <Ticket className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Your Cart is Empty</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Browse events and add tickets to get started.</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
          >
            Browse Events
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Back Link */}
      {cartEventSlug && (
        <Link
          to={`/events/${cartEventSlug}`}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Event
        </Link>
      )}

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Buyer Info Form */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-5">Your Information</h2>
            <form id="checkout-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('buyerName')}
                  type="text"
                  placeholder="John Doe"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-white"
                />
                {errors.buyerName && (
                  <p className="mt-1 text-sm text-red-500">{errors.buyerName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('buyerEmail')}
                  type="email"
                  placeholder="john@example.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-white"
                />
                {errors.buyerEmail && (
                  <p className="mt-1 text-sm text-red-500">{errors.buyerEmail.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">Tickets will be sent to this email</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Phone Number
                </label>
                <input
                  {...register('buyerPhone')}
                  type="tel"
                  placeholder="+974 1234 5678"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-white"
                />
              </div>
            </form>
          </div>

          {/* Coupon Section */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4" /> Promo Code
            </h3>
            {couponCode ? (
              <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3">
                <div>
                  <span className="text-sm font-semibold text-green-700 dark:text-green-400">{couponCode}</span>
                  <span className="text-xs text-green-600 dark:text-green-500 ml-2">
                    -{formatCurrency(discount, cartItems[0]?.currency || 'USD')}
                  </span>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponInput.trim()}
                  className="px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-5">Order Summary</h2>

            <div className="space-y-4 mb-5">
              {cartItems.map(item => (
                <div key={item.ticketTypeId} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.ticketTypeName}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => updateQuantity(item.ticketTypeId, item.quantity - 1)}
                        className="w-7 h-7 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-medium w-6 text-center text-gray-900 dark:text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.ticketTypeId, Math.min(item.quantity + 1, item.maxPerOrder, item.availableCount))}
                        disabled={item.quantity >= item.maxPerOrder || item.quantity >= item.availableCount}
                        className="w-7 h-7 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.ticketTypeId)}
                        className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {formatCurrency(item.unitPrice * item.quantity, item.currency)}
                  </span>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(subtotal, cartItems[0]?.currency || 'USD')}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Discount</span>
                  <span className="text-green-600">-{formatCurrency(discount, cartItems[0]?.currency || 'USD')}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-100 dark:border-gray-800">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(total, cartItems[0]?.currency || 'USD')}</span>
              </div>
            </div>

            <button
              type="submit"
              form="checkout-form"
              disabled={submitting}
              className="w-full mt-6 py-3.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-lg shadow-primary-600/25 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : total === 0 ? (
                'Complete Order (Free)'
              ) : (
                `Pay ${formatCurrency(total, cartItems[0]?.currency || 'USD')}`
              )}
            </button>

            <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-gray-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Secure checkout powered by Tap
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
